import { TicketPriority } from '@21k/shared';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  createSlaRule,
  getApiErrorMessage,
  getSlaRule,
  listDepartments,
  listTicketCategoryOptions,
  updateSlaRule,
} from '../../api/client';
import type { DepartmentSummary, TicketCategoryRef } from '../../api/types';
import { useAuth } from '../../contexts/AuthContext';
import { canEditSla, canWriteSla } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

const PRIORITIES = Object.values(TicketPriority);

export default function SlaRuleFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [categories, setCategories] = useState<TicketCategoryRef[]>([]);
  const [departmentId, setDepartmentId] = useState(searchParams.get('departmentId') ?? '');
  const [categoryId, setCategoryId] = useState('');
  const [priority, setPriority] = useState<TicketPriority>(TicketPriority.MEDIUM);
  const [firstResponseMinutes, setFirstResponseMinutes] = useState(60);
  const [resolutionMinutes, setResolutionMinutes] = useState(480);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit = currentUser && (isNew ? canWriteSla(currentUser) : canEditSla(currentUser));

  useEffect(() => {
    Promise.all([listDepartments(), listTicketCategoryOptions()])
      .then(([depts, cats]) => {
        setDepartments(depts);
        setCategories(cats);
        if (isNew && !departmentId && depts[0]) setDepartmentId(depts[0].id);
        if (isNew && !categoryId && cats[0]) setCategoryId(cats[0].id);
      })
      .catch(() => undefined);
  }, [isNew, departmentId, categoryId]);

  useEffect(() => {
    if (isNew || !id) return;
    getSlaRule(id)
      .then((r) => {
        setDepartmentId(r.departmentId);
        setCategoryId(r.categoryId);
        setPriority(r.priority);
        setFirstResponseMinutes(r.firstResponseMinutes);
        setResolutionMinutes(r.resolutionMinutes);
        setIsActive(r.isActive);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await createSlaRule({
          departmentId,
          categoryId,
          priority,
          firstResponseMinutes,
          resolutionMinutes,
          isActive,
        });
        navigate(PLATFORM_CONFIG.sla);
      } else if (id) {
        await updateSlaRule(id, {
          firstResponseMinutes,
          resolutionMinutes,
          isActive,
        });
        navigate(PLATFORM_CONFIG.sla);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) {
    return (
      <div className="alert alert-error">
        You do not have permission to manage SLA rules.{' '}
        <Link to={PLATFORM_CONFIG.sla}>Back to list</Link>
      </div>
    );
  }

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <Link to={PLATFORM_CONFIG.sla} style={{ fontSize: '0.85rem' }}>
            ← SLA rules
          </Link>
          <h1 style={{ marginTop: '0.35rem' }}>{isNew ? 'New SLA rule' : 'Edit SLA rule'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="card card-body" style={{ maxWidth: 560 }} onSubmit={handleSave}>
        {isNew ? (
          <>
            <div className="form-group">
              <label htmlFor="sla-dept">Department</label>
              <select
                id="sla-dept"
                className="form-control"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                required
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sla-cat">Ticket category</label>
                <select
                  id="sla-cat"
                  className="form-control"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="sla-pri">Priority</label>
                <select
                  id="sla-pri"
                  className="form-control"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TicketPriority)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        ) : (
          <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
            Department, category, and priority cannot be changed after creation. Delete and recreate
            if needed.
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="sla-fr">First response (minutes)</label>
            <input
              id="sla-fr"
              type="number"
              min={1}
              className="form-control"
              value={firstResponseMinutes}
              onChange={(e) => setFirstResponseMinutes(Number(e.target.value))}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="sla-res">Resolution (minutes)</label>
            <input
              id="sla-res"
              type="number"
              min={1}
              className="form-control"
              value={resolutionMinutes}
              onChange={(e) => setResolutionMinutes(Number(e.target.value))}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />{' '}
            Active
          </label>
        </div>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create rule' : 'Save changes'}
        </button>
      </form>
    </>
  );
}
